/**
 * Awaab's Law — full-tab entry point (previously only reachable via the
 * small house-crack icon in the Tenants list, same as Messaging has its
 * own tab rather than just an inline icon).
 *
 * Reuses the existing AwaabPanel modal unchanged for the actual case
 * workflow (hazard triage, investigation, repair/preventative work,
 * accommodation) — this page's job is discoverability: a portfolio
 * summary plus a tenant/property picker so a user can get into a case
 * without first navigating to Tenants and hunting for the icon.
 */
import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import { awaabService, tenantService } from '../../services/api';
import AwaabPanel from './AwaabPanel';
import SafeguardingPanel from '../RSL/SafeguardingPanel';
import CSVImportModal from '../Shared/CSVImportModal';
import { attachEvidenceFromZip } from '../../utils/bulkEvidenceUpload';

interface Tenant {
    id: string;
    name: string;
    email: string;
    propertyId: string;
}

interface AttentionCase {
    caseId: string;
    hazardType: string;
    classification: string;
    worstStatus: 'BREACHED' | 'AT_RISK';
    earliestDue: string | null;
}

export default function AwaabLawPage() {
    const [dashboard, setDashboard] = useState<any>(null);
    const [attention, setAttention] = useState<AttentionCase[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [tenantsLoading, setTenantsLoading] = useState(true);
    const [search, setSearch] = useState('');

    const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
    const [showPanel, setShowPanel] = useState(false);
    const [selectedTenantForSafeguarding, setSelectedTenantForSafeguarding] = useState<Tenant | null>(null);
    const [showSafeguardingPanel, setShowSafeguardingPanel] = useState(false);

    // "Report or open a hazard case" CTA — opens blank, tenant search only,
    // so a new report can be associated with a tenant in one step instead of
    // scrolling down to the always-visible picker below.
    const [showQuickPicker, setShowQuickPicker] = useState(false);
    const [quickSearch, setQuickSearch] = useState('');
    const [showBulkImport, setShowBulkImport] = useState(false);

    useEffect(() => {
        let cancelled = false;
        awaabService.getPortfolioDashboard()
            .then((res: any) => {
                if (cancelled) return;
                const data = res.data?.data;
                setDashboard({
                    open: data?.portfolio?.totalOpenCases,
                    breached: data?.slaHealth?.breached,
                    atRisk: data?.slaHealth?.atRisk,
                    hhsrsCat1: data?.hhsrsCat1Open,
                });
                setAttention(data?.attentionRequired || []);
            })
            .catch(() => {
                if (cancelled) return;
                awaabService.getDashboard()
                    .then((res: any) => { if (!cancelled) setDashboard(res.data?.data || null); })
                    .catch((e: any) => { if (!cancelled) setError(e?.response?.data?.message || 'Failed to load Awaab dashboard data'); });
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        tenantService.getTenants()
            .then((res: any) => { if (!cancelled) setTenants(res.data?.data || res.data || []); })
            .catch(() => { if (!cancelled) setTenants([]); })
            .finally(() => { if (!cancelled) setTenantsLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const filteredTenants = useMemo(() => {
        if (!search.trim()) return tenants.slice(0, 12);
        const q = search.toLowerCase();
        return tenants.filter((t) => t.name?.toLowerCase().includes(q) || t.email?.toLowerCase().includes(q)).slice(0, 12);
    }, [tenants, search]);

    const quickFilteredTenants = useMemo(() => {
        if (!quickSearch.trim()) return [];
        const q = quickSearch.toLowerCase();
        return tenants.filter((t) => t.name?.toLowerCase().includes(q) || t.email?.toLowerCase().includes(q)).slice(0, 8);
    }, [tenants, quickSearch]);

    const openTenant = (tenant: Tenant) => {
        setSelectedTenant(tenant);
        setShowPanel(true);
    };

    const openSafeguarding = (tenant: Tenant) => {
        setSelectedTenantForSafeguarding(tenant);
        setShowSafeguardingPanel(true);
    };

    const openTenantFromQuickPicker = (tenant: Tenant) => {
        setShowQuickPicker(false);
        setQuickSearch('');
        openTenant(tenant);
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <Navbar />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-2 flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">
                            <i className="fas fa-house-chimney-crack text-orange-600 mr-2"></i>Awaab's Law
                        </h1>
                        <p className="text-slate-500 mt-1">Hazard case management under the Hazards in Social Housing (Prescribed Requirements) (England) Regulations 2025 — investigation, safety work, and written summaries.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowBulkImport(true)}
                            title="Bulk upload existing hazard cases from a CSV"
                            className="shrink-0 inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-orange-700 font-semibold px-4 py-2.5 rounded-xl shadow-sm border border-orange-200 transition-colors"
                        >
                            <i className="fas fa-file-csv"></i> Bulk Upload
                        </button>
                        <button
                            onClick={() => setShowQuickPicker(true)}
                            className="shrink-0 inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-colors"
                        >
                            <i className="fas fa-plus"></i> Report or open a hazard case
                        </button>
                    </div>
                </div>

                {attention.length > 0 && (
                    <div className="mt-6">
                        <h3 className="text-lg font-bold text-slate-900 mb-3">Needs attention now</h3>
                        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                            {attention.slice(0, 10).map((c) => (
                                <div key={c.caseId} className="flex items-center justify-between px-5 py-3">
                                    <div>
                                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${c.worstStatus === 'BREACHED' ? 'bg-red-500' : 'bg-amber-500'}`}></span>
                                        <span className="font-medium text-slate-800">{c.hazardType}</span>
                                        <span className="text-slate-400 text-sm ml-2">{c.classification}</span>
                                    </div>
                                    <div className="text-sm text-slate-500">{c.earliestDue ? new Date(c.earliestDue).toLocaleDateString('en-GB') : '—'}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 text-slate-400 text-sm">Loading portfolio summary…</div>
                ) : error ? (
                    <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 text-red-600 text-sm">{error}</div>
                ) : (
                    <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                            <div className="text-3xl font-bold text-slate-900">{dashboard?.open ?? '—'}</div>
                            <div className="text-sm text-slate-500 mt-1">Open hazard cases</div>
                        </div>
                        <div className="bg-white rounded-xl border border-red-200 p-5">
                            <div className="text-3xl font-bold text-red-600">{dashboard?.breached ?? '—'}</div>
                            <div className="text-sm text-slate-500 mt-1">SLA breaches</div>
                        </div>
                        <div className="bg-white rounded-xl border border-amber-200 p-5">
                            <div className="text-3xl font-bold text-amber-600">{dashboard?.atRisk ?? '—'}</div>
                            <div className="text-sm text-slate-500 mt-1">At risk of breach</div>
                        </div>
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                            <div className="text-3xl font-bold text-slate-900">{dashboard?.hhsrsCat1 ?? '—'}</div>
                            <div className="text-sm text-slate-500 mt-1">Open HHSRS Cat 1</div>
                        </div>
                    </div>
                )}

                {/* Primary action — same entry point the small icon in the Tenants
                    list opens (AwaabPanel). */}
                <div id="awaab-tenant-picker" className="mt-8 bg-white rounded-2xl border border-orange-200 shadow-sm p-5">
                    <h3 className="text-base font-bold text-slate-900 mb-1">Find a tenant to report or manage a hazard case</h3>
                    <p className="text-sm text-slate-500 mb-4">Search for the tenant, then click them to open their hazard cases — report a new one, continue an investigation, or log repair/preventative work.</p>
                    <div className="relative mb-4">
                        <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by tenant name or email…"
                            className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                        />
                    </div>

                    {tenantsLoading ? (
                        <div className="text-sm text-slate-400 py-4">Loading tenants…</div>
                    ) : filteredTenants.length === 0 ? (
                        <div className="text-sm text-slate-400 py-4">No matching tenants.</div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {filteredTenants.map((t) => (
                                <div key={t.id} className="flex items-center justify-between px-2 py-3 hover:bg-slate-50 rounded-lg transition-colors">
                                    <div>
                                        <div className="text-sm font-medium text-slate-800">{t.name}</div>
                                        <div className="text-xs text-slate-400">{t.email}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => openSafeguarding(t)}
                                            title="Safeguarding & Pre-Action Protocol"
                                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 px-3 py-1.5 rounded-lg transition-colors"
                                        >
                                            <i className="fas fa-shield-halved"></i> Safeguarding
                                        </button>
                                        <button
                                            onClick={() => openTenant(t)}
                                            title="Awaab's Law Hazard Management"
                                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-200 px-3 py-1.5 rounded-lg transition-colors"
                                        >
                                            <i className="fas fa-house-chimney-crack"></i> Hazard Management
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {showQuickPicker && (
                <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
                    <div className="flex items-start justify-center min-h-screen pt-24 px-4 pb-20">
                        <div
                            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                            aria-hidden="true"
                            onClick={() => { setShowQuickPicker(false); setQuickSearch(''); }}
                        ></div>
                        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg p-5">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h3 className="text-base font-bold text-slate-900">Report or open a hazard case</h3>
                                    <p className="text-sm text-slate-500 mt-0.5">Search for the tenant to associate this report with.</p>
                                </div>
                                <button
                                    onClick={() => { setShowQuickPicker(false); setQuickSearch(''); }}
                                    className="text-slate-400 hover:text-slate-600"
                                >
                                    <i className="fas fa-xmark"></i>
                                </button>
                            </div>
                            <div className="relative mb-3">
                                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                                <input
                                    type="text"
                                    value={quickSearch}
                                    onChange={(e) => setQuickSearch(e.target.value)}
                                    placeholder="Search by tenant name or email…"
                                    className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                                    autoFocus
                                />
                            </div>
                            {!quickSearch.trim() ? (
                                <div className="text-sm text-slate-400 py-6 text-center">Start typing a tenant's name or email.</div>
                            ) : quickFilteredTenants.length === 0 ? (
                                <div className="text-sm text-slate-400 py-6 text-center">No matching tenants.</div>
                            ) : (
                                <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                                    {quickFilteredTenants.map((t) => (
                                        <button
                                            key={t.id}
                                            onClick={() => openTenantFromQuickPicker(t)}
                                            className="w-full flex items-center justify-between px-2 py-3 hover:bg-slate-50 rounded-lg transition-colors text-left"
                                        >
                                            <div>
                                                <div className="text-sm font-medium text-slate-800">{t.name}</div>
                                                <div className="text-xs text-slate-400">{t.email}</div>
                                            </div>
                                            <i className="fas fa-chevron-right text-slate-300 text-xs"></i>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <AwaabPanel isOpen={showPanel} onClose={() => setShowPanel(false)} tenant={selectedTenant} />
            <SafeguardingPanel isOpen={showSafeguardingPanel} onClose={() => setShowSafeguardingPanel(false)} tenant={selectedTenantForSafeguarding} />

            <CSVImportModal
                isOpen={showBulkImport}
                onClose={() => setShowBulkImport(false)}
                onImport={async (data, zipFile) => {
                    const HAZARD_TYPES = ['DAMP_MOULD', 'EXCESS_COLD', 'EXCESS_HEAT', 'FIRE', 'ELECTRICAL', 'STRUCTURAL_COLLAPSE', 'FALLS', 'FLOOD_WATER_ESCAPE', 'SEWAGE_LEAK', 'GAS_LEAK', 'VERMIN_INFESTATION', 'WATER_SUPPLY', 'FOOD_SAFETY', 'PERSONAL_HYGIENE', 'ASBESTOS', 'ENTRY_BY_INTRUDERS', 'OTHER'];
                    let rows: any[] = data.map((row: any, idx: number) => {
                        const tenantEmail = String(row.tenant_email || '').toLowerCase().trim();
                        const tenant = tenants.find((t) => t.email?.toLowerCase() === tenantEmail);
                        if (!tenant) throw new Error(`Row ${idx + 1}: no tenant found with email "${row.tenant_email}"`);
                        const hazardType = String(row.hazard_type || '').toUpperCase().trim();
                        if (!HAZARD_TYPES.includes(hazardType)) {
                            throw new Error(`Row ${idx + 1}: hazard_type must be one of ${HAZARD_TYPES.join(', ')}`);
                        }
                        return {
                            tenantId: tenant.id,
                            propertyId: tenant.propertyId,
                            hazardType,
                            description: row.description || '',
                            reportChannel: row.report_channel || 'PORTAL',
                            dayZero: row.date_reported || undefined,
                            evidence_filename: row.evidence_filename || '',
                        };
                    });
                    rows = await attachEvidenceFromZip(rows, zipFile, {
                        filenameField: 'evidence_filename',
                        resolvePropertyId: (row) => row.propertyId,
                    });
                    const cases = rows.map(({ evidence_filename, _attachStatus, ...rest }) => rest);
                    await awaabService.bulkImportCases(cases);
                }}
                title="Bulk Upload Hazard Cases"
                headerRequirements={[
                    { id: 'tenant_email', label: 'Tenant Email', required: true, description: 'Must match an existing tenant', example: 'jane.doe@example.com' },
                    { id: 'hazard_type', label: 'Hazard Type', required: true, description: 'e.g. DAMP_MOULD, EXCESS_COLD, FIRE, ELECTRICAL', example: 'DAMP_MOULD' },
                    { id: 'description', label: 'Description', required: false, description: 'What the hazard is', example: 'Black mould in bathroom ceiling' },
                    { id: 'report_channel', label: 'Report Channel', required: false, description: 'PORTAL, EMAIL, SMS, PHONE etc. (defaults to PORTAL)', example: 'PORTAL' },
                    { id: 'date_reported', label: 'Date Reported', required: false, description: 'YYYY-MM-DD', example: '2026-01-15' },
                    { id: 'evidence_filename', label: 'Evidence Filename', required: false, description: 'Filename inside the companion .zip to attach as this case\'s photo/document', example: 'mould_photo.jpg' },
                ]}
                templateHeaders={['tenant_email', 'hazard_type', 'description', 'report_channel', 'date_reported', 'evidence_filename']}
                sampleData="jane.doe@example.com,DAMP_MOULD,Black mould in bathroom ceiling,PORTAL,2026-01-15,mould_photo.jpg"
                allowFileAttachments
            />

            <Footer />
        </div>
    );
}
